import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Star, StarOff, ExternalLink, Loader2, Sparkles, Linkedin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, resolveImageUrl } from "../../lib/api";

const CATEGORY_TONE = {
  "Educational Insights": "#FF8C00",
  "Faculty Corner": "#00FFD0",
  "Parent Zone": "#FFB05E",
  "Founder's Journal": "#FF5A00",
};

export default function AdminBlogList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null); // slug currently being acted on

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/blog/posts", { params: { limit: 200 } });
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleSpotlight = async (post) => {
    setWorking(post.slug);
    try {
      await api.patch(`/blog/posts/${post.slug}/spotlight`, null, {
        params: { in_spotlight: !post.in_spotlight },
      });
      toast.success(post.in_spotlight ? "Spotlight removed." : `"${post.title}" is now in Spotlight.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(null);
    }
  };

  const onDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    setWorking(post.slug);
    try {
      await api.delete(`/blog/posts/${post.slug}`);
      toast.success("Post deleted.");
      setPosts((prev) => prev.filter((p) => p.slug !== post.slug));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF8C00]">Blog</p>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white">All posts</h1>
          <p className="mt-2 text-sm text-[#A1A1AA]">{posts.length} post{posts.length === 1 ? "" : "s"} · 1 spotlight slot</p>
        </div>
        <button
          onClick={() => navigate("/admin/blog/new")}
          data-testid="admin-new-post"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FF8C00] text-[#0A0A0C] text-sm font-black uppercase tracking-widest hover:bg-[#FFA333]"
        >
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-16 flex items-center justify-center text-[#A1A1AA]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading posts…
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-12 text-center">
          <p className="text-[#A1A1AA]">No posts yet.</p>
          <button
            onClick={() => navigate("/admin/blog/new")}
            className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FF8C00] text-[#0A0A0C] text-sm font-bold"
          >
            <Plus className="w-4 h-4" /> Create your first post
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/8 bg-[#0C0C10] overflow-hidden" data-testid="admin-blog-table">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/8 text-[10px] font-mono-tech uppercase tracking-widest text-[#6a6a70]">
            <div className="col-span-1">Cover</div>
            <div className="col-span-5">Title</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-white/8">
            {posts.map((p) => {
              const tone = CATEGORY_TONE[p.category] || "#FF8C00";
              const cover = resolveImageUrl(p.cover_image);
              const busy = working === p.slug;
              return (
                <div
                  key={p.slug}
                  data-testid={`admin-blog-row-${p.slug}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center px-5 py-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="md:col-span-1">
                    {cover ? (
                      <img src={cover} alt="" className="w-16 h-12 object-cover rounded-md border border-white/8" />
                    ) : (
                      <div className="w-16 h-12 bg-white/5 rounded-md border border-white/8" />
                    )}
                  </div>
                  <div className="md:col-span-5 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{p.title}</p>
                    <p className="mt-0.5 text-xs text-[#6a6a70] font-mono-tech truncate">/{p.slug} · {p.read_minutes} min · {p.author}</p>
                  </div>
                  <div className="md:col-span-2">
                    <span
                      className="text-[10px] font-mono-tech font-black uppercase tracking-widest px-2 py-1 rounded-md inline-block"
                      style={{ background: `${tone}1A`, border: `1px solid ${tone}55`, color: tone }}
                    >
                      {p.category}
                    </span>
                  </div>
                  <div className="md:col-span-2">
                    {p.in_spotlight ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono-tech font-black uppercase tracking-widest text-[#FF8C00]">
                        <Sparkles className="w-3 h-3" /> Spotlight
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[#6a6a70]">Published</span>
                    )}
                  </div>
                  <div className="md:col-span-2 flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => toggleSpotlight(p)}
                      disabled={busy}
                      title={p.in_spotlight ? "Remove from spotlight" : "Set as spotlight"}
                      data-testid={`toggle-spotlight-${p.slug}`}
                      className="p-2 rounded-md hover:bg-white/5 text-[#A1A1AA] hover:text-[#FF8C00] disabled:opacity-50"
                    >
                      {p.in_spotlight ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => copyLinkedInUrl(p)}
                      title="Copy LinkedIn-ready share URL"
                      data-testid={`linkedin-${p.slug}`}
                      className="p-2 rounded-md hover:bg-[#0a66c2]/15 text-[#A1A1AA] hover:text-[#0a66c2]"
                    >
                      <Linkedin className="w-4 h-4" />
                    </button>
                    <Link
                      to={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noopener"
                      title="View on site"
                      data-testid={`view-${p.slug}`}
                      className="p-2 rounded-md hover:bg-white/5 text-[#A1A1AA] hover:text-white"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                    <Link
                      to={`/admin/blog/edit/${p.slug}`}
                      title="Edit"
                      data-testid={`edit-${p.slug}`}
                      className="p-2 rounded-md hover:bg-white/5 text-[#A1A1AA] hover:text-[#FF8C00]"
                    >
                      <Pencil className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => onDelete(p)}
                      disabled={busy}
                      title="Delete"
                      data-testid={`delete-${p.slug}`}
                      className="p-2 rounded-md hover:bg-red-500/10 text-[#A1A1AA] hover:text-red-400 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
